import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import {
  LayoutDashboard,
  Bell,
  CreditCard,
  FileSpreadsheet,
  Users,
  BookOpen,
  LogOut,
  MapPin,
  User,
  ShieldCheck,
  FolderArchive,
  ShoppingBag,
  Package,
  Building,
  Heart,
  ChevronDown,
  ChevronRight,
  Settings,
  Lock,
  Award,
  Trophy,
  FileText
} from 'lucide-react';

import { ToastProvider, useToast } from './components/common/Toast';
import { Navbar } from './components/layout/Navbar';
import { LandingPage } from './pages/public/LandingPage';
import { LoginPage } from './pages/auth/LoginPage';
import { HistoryPage } from './pages/public/HistoryPage';
import { ContactPage } from './pages/public/ContactPage';
import { PublicSyllabusPage } from './pages/public/PublicSyllabusPage';
import { AboutPage } from './pages/public/AboutPage';
import { OrgChartPage } from './pages/public/OrgChartPage';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { NoticeManagementPage } from './pages/notices/NoticeManagementPage';
import { PaymentPage } from './pages/payments/PaymentPage';
import { AuditReportPage } from './pages/financial/AuditReportPage';
import { MemberDirectoryPage } from './pages/members/MemberDirectoryPage';
import { RolesManagementPage } from './pages/members/RolesManagementPage';
import { SyllabusPage } from './pages/syllabus/SyllabusPage';
import { PengurusanSilibusPage } from './pages/syllabus/PengurusanSilibusPage';
import { AccountPage } from './pages/account/AccountPage';
import { DocumentArchivePage } from './pages/documents/DocumentArchivePage';
import { GrantManagementPage } from './pages/financial/GrantManagementPage';
import { AssetRentalPage } from './pages/financial/AssetRentalPage';
import { MerchandisePage } from './pages/merchandise/MerchandisePage';
import { PembelianPage } from './pages/pembelian/PembelianPage';
import { InventoriPage } from './pages/inventori/InventoriPage';
import { KeluargaPage } from './pages/keluarga/KeluargaPage';
import { GelanggangPage } from './pages/gelanggang/GelanggangPage';
import { DonationPublicPage } from './pages/public/DonationPublicPage';
import { DonationsManagementPage } from './pages/financial/DonationsManagementPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { canUserAddChild, isSuperAdmin, hasRolePermission, isProfileComplete, isManagementOrTrainer, isWarisMissingChild, isPaymentPendingAdminConfirmation, getOnboardingPaymentStatus } from './utils/formatters';
import { RoleSwitcherFloating } from './components/common/RoleSwitcherFloating';
import { TaglinePhilosophyModal } from './components/common/TaglinePhilosophyModal';
import { DonationLiveToastPopup } from './components/donations/DonationLiveToastPopup';
import { CompleteProfilePage } from './pages/auth/CompleteProfilePage';
import { OnboardingPaymentPage } from './pages/auth/OnboardingPaymentPage';
import { WarisChildRequiredPage } from './pages/auth/WarisChildRequiredPage';
import { isNewAhliPendingOnboarding } from './utils/pricing';
import { getInvoicesDB, getMembersDB } from './services/db';
import { InvoiceItem, UserProfile } from './types';
import { SEO } from './components/common/SEO';
import { BengkungCulaBadge } from './components/common/BengkungCulaBadge';
import { getSilatRankFromBengkung } from './data/bengkungRanks';
import { UjianBengkungPage } from './pages/public/UjianBengkungPage';
import { UjianGradingPage } from './pages/grading/UjianGradingPage';
import { UjianGradingSettingsPage } from './pages/grading/UjianGradingSettingsPage';
import { UjianGradingLeaderboardPage } from './pages/grading/UjianGradingLeaderboardPage';
import { UjianBertulisPage } from './pages/grading/UjianBertulisPage';
import { getStoredUserSession } from './utils/sessionVault';
import { PushSubscribePrompt } from './components/common/PushSubscribePrompt';
import { SOCIAL_LINKS } from './data/socialLinks';
import { resetScrollToTop } from './utils/scrollRestoration';

const VALID_TABS = ['landing', 'login', 'register', 'dashboard', 'notices', 'payments', 'audit', 'grants', 'assets', 'members', 'roles', 'syllabus', 'pengurusan-silibus', 'syllabus-manage', 'kurikulum', 'grading', 'ujian-grading', 'grading-settings', 'grading-leaderboard', 'leaderboard', 'ujian-bertulis', 'account', 'dokumen', 'tentang', 'organisasi', 'sejarah', 'silibus-awam', 'hubungi', 'merchandise', 'pembelian', 'inventory', 'keluarga', 'gelanggang', 'donations', 'donasi', 'tabung', 'settings', 'ujian-bengkung', 'ujian', 'onboarding-payment', 'onboarding'];
const PUBLIC_TABS = ['landing', 'login', 'register', 'tentang', 'organisasi', 'sejarah', 'silibus-awam', 'hubungi', 'payments', 'dokumen', 'merchandise', 'donations', 'donasi', 'ujian-bengkung', 'ujian', 'onboarding-payment', 'onboarding'];

const AppContent: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [isTaglineModalOpen, setIsTaglineModalOpen] = useState(false);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [isMembersLoaded, setIsMembersLoaded] = useState(false);

  useEffect(() => {
    if (!currentUser) return;
    const loadInvoices = () => {
      getInvoicesDB().then(setInvoices).catch(() => { });
    };
    const loadMembers = () => {
      getMembersDB()
        .then((data) => {
          setMembers(data);
          setIsMembersLoaded(true);
        })
        .catch(() => { });
    };

    loadInvoices();
    loadMembers();

    window.addEventListener('asmm_db_updated', loadInvoices);
    window.addEventListener('asmm_db_updated', loadMembers);
    return () => {
      window.removeEventListener('asmm_db_updated', loadInvoices);
      window.removeEventListener('asmm_db_updated', loadMembers);
    };
  }, [currentUser]);

  const isPaymentPending = Boolean(currentUser && isPaymentPendingAdminConfirmation(currentUser, invoices, members));
  const paymentStatus = getOnboardingPaymentStatus(currentUser, invoices, members);

  // Read initial route from URL pathname (HTML5 History API)
  const getInitialTab = (): string => {
    const rawSearch = window.location.search || (window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : '');
    const searchParams = new URLSearchParams(rawSearch);
    if (searchParams.has('inv') || searchParams.has('invoice')) {
      return 'payments';
    }
    const rawPath = window.location.pathname.replace(/^\//, '').split('?')[0].trim();
    const hashPath = window.location.hash.replace('#', '').trim();
    const path = rawPath || hashPath;

    if (path.startsWith('members/roles') || path.startsWith('roles')) {
      return 'roles';
    }
    if (path.startsWith('grading/settings') || path.startsWith('grading/tetapan')) {
      return 'grading-settings';
    }
    if (path.startsWith('grading/ujian-bertulis') || path.startsWith('ujian-bertulis')) {
      return 'ujian-bertulis';
    }
    if (path.startsWith('grading/leaderboard') || path.startsWith('grading/kedudukan') || path.startsWith('grading/papan-skor') || path === 'leaderboard') {
      return 'grading-leaderboard';
    }
    if (path.startsWith('syllabus/manage') || path.startsWith('pengurusan-silibus') || path.startsWith('kurikulum')) {
      return 'pengurusan-silibus';
    }

    const firstSegment = path.split('/')[0];
    if (firstSegment && VALID_TABS.includes(firstSegment)) {
      // If user is accessing a protected tab while unauthenticated, preserve the target URL and send to login
      const savedUser = typeof window !== 'undefined' ? getStoredUserSession() : null;
      const isLoggedOut = typeof window !== 'undefined' && (
        localStorage.getItem('asmm_user_logged_out') === 'true' ||
        sessionStorage.getItem('asmm_user_logged_out') === 'true'
      );
      const isAuthenticated = Boolean(savedUser && !isLoggedOut);

      if (!PUBLIC_TABS.includes(firstSegment) && !isAuthenticated) {
        const fullIntended = window.location.pathname + window.location.search;
        if (fullIntended && fullIntended !== '/' && !fullIntended.startsWith('/login') && !fullIntended.startsWith('/register')) {
          sessionStorage.setItem('asmm_intended_url', fullIntended);
        }
        return 'login';
      }
      return firstSegment;
    }
    return 'landing';
  };

  const [activeTab, setActiveTabState] = useState<string>(getInitialTab);

  const handleTabChange = useCallback((tab: string, fullUrl?: string) => {
    if (VALID_TABS.includes(tab)) {
      // Gate access if user has pending onboarding payment confirmation
      const isPending = Boolean(currentUser && isPaymentPendingAdminConfirmation(currentUser, invoices, members));
      const ALLOWED_WHEN_PENDING = ['dashboard', 'payments', 'account', ...PUBLIC_TABS];

      if (currentUser && isPending && !ALLOWED_WHEN_PENDING.includes(tab)) {
        showToast(
          'warning',
          'Akses Terhad (Perlu Pengesahan Bayaran)',
          'Ciri ini dikunci sehingga pembayaran yuran pendaftaran disahkan oleh pentadbir. Sila lengkapkan pembayaran di menu Bayaran.'
        );
        tab = 'payments';
        fullUrl = '/payments';
      }

      setActiveTabState(tab);
      const targetPath = fullUrl || (
        tab === 'landing'
          ? '/'
          : tab === 'settings'
            ? (window.location.pathname.startsWith('/settings/') ? window.location.pathname : '/settings/alert')
            : `/${tab}`
      );
      if (window.location.pathname + window.location.search !== targetPath) {
        window.history.pushState(null, '', targetPath);
        window.dispatchEvent(new PopStateEvent('popstate'));
      } else {
        resetScrollToTop();
      }
    }
  }, [currentUser, invoices, members, showToast]);

  // Post-login redirect: navigates to intended URL or sends new/pending users to bayar
  const navigateAfterLogin = useCallback(() => {
    // If onboarding payment is pending admin confirmation, direct user straight to bayar (/payments)
    const isPending = Boolean(currentUser && isPaymentPendingAdminConfirmation(currentUser, invoices, members));
    if (isPending) {
      handleTabChange('payments');
      return;
    }

    const intended = typeof window !== 'undefined' ? sessionStorage.getItem('asmm_intended_url') : null;
    if (intended) {
      sessionStorage.removeItem('asmm_intended_url');
      try {
        const urlObj = new URL(intended, window.location.origin);
        const rawPath = urlObj.pathname.replace(/^\//, '').trim();
        const firstSeg = rawPath.split('/')[0];
        const tab = firstSeg && VALID_TABS.includes(firstSeg) ? firstSeg : 'dashboard';
        const fullUrl = urlObj.pathname + urlObj.search;
        handleTabChange(tab, fullUrl);
        return;
      } catch {
        // Fallback to dashboard
      }
    }
    handleTabChange('dashboard');
  }, [currentUser, invoices, members, handleTabChange]);

  useEffect(() => {
    const handlePopState = () => {
      const rawPath = window.location.pathname.replace(/^\//, '').trim();
      if (rawPath.startsWith('members/roles') || rawPath.startsWith('roles')) {
        setActiveTabState('roles');
        return;
      }
      if (rawPath.startsWith('grading/settings') || rawPath.startsWith('grading/tetapan')) {
        setActiveTabState('grading-settings');
        return;
      }
      if (rawPath.startsWith('grading/ujian-bertulis') || rawPath.startsWith('ujian-bertulis')) {
        setActiveTabState('ujian-bertulis');
        return;
      }
      if (rawPath.startsWith('grading/leaderboard') || rawPath.startsWith('grading/kedudukan') || rawPath.startsWith('grading/papan-skor') || rawPath === 'leaderboard') {
        setActiveTabState('grading-leaderboard');
        return;
      }
      if (rawPath.startsWith('syllabus/manage') || rawPath.startsWith('pengurusan-silibus') || rawPath.startsWith('kurikulum')) {
        setActiveTabState('pengurusan-silibus');
        return;
      }
      const firstSegment = rawPath.split('/')[0];
      const targetTab = firstSegment === '' ? 'landing' : firstSegment;
      if (VALID_TABS.includes(targetTab)) {
        setActiveTabState(targetTab);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Synchronize URL path on initial load & tab changes
  useEffect(() => {
    if (activeTab === 'landing') {
      if (window.location.pathname !== '/') {
        window.history.replaceState(null, '', '/');
      }
    } else if (activeTab === 'account') {
      if (!window.location.pathname.startsWith('/account')) {
        window.history.replaceState(null, '', '/account');
      }
    } else if (activeTab === 'inventory') {
      if (!window.location.pathname.startsWith('/inventory')) {
        window.history.replaceState(null, '', '/inventory');
      }
    } else if (activeTab === 'merchandise') {
      if (!window.location.pathname.startsWith('/merchandise')) {
        window.history.replaceState(null, '', '/merchandise');
      }
    } else if (activeTab === 'keluarga') {
      if (!window.location.pathname.startsWith('/keluarga')) {
        window.history.replaceState(null, '', '/keluarga');
      }
    } else if (activeTab === 'members') {
      if (!window.location.pathname.startsWith('/members')) {
        window.history.replaceState(null, '', '/members');
      }
    } else if (activeTab === 'roles') {
      if (!window.location.pathname.startsWith('/roles') && !window.location.pathname.startsWith('/members/roles')) {
        window.history.replaceState(null, '', '/roles');
      }
    } else if (activeTab === 'donations' || activeTab === 'donasi') {
      if (!window.location.pathname.startsWith('/donations') && !window.location.pathname.startsWith('/donasi')) {
        window.history.replaceState(null, '', `/${activeTab}`);
      }
    } else if (activeTab === 'tabung') {
      if (!window.location.pathname.startsWith('/tabung')) {
        window.history.replaceState(null, '', '/tabung');
      }
    } else if (activeTab === 'settings') {
      if (!window.location.pathname.startsWith('/settings')) {
        window.history.replaceState(null, '', '/settings/alert');
      }
    } else if (activeTab === 'grading-settings') {
      if (window.location.pathname !== '/grading/settings' && window.location.pathname !== '/grading/tetapan') {
        window.history.replaceState(null, '', '/grading/settings');
      }
    } else if (activeTab === 'ujian-bertulis') {
      if (window.location.pathname !== '/ujian-bertulis' && window.location.pathname !== '/grading/ujian-bertulis') {
        window.history.replaceState(null, '', '/ujian-bertulis');
      }
    } else if (activeTab === 'grading-leaderboard' || activeTab === 'leaderboard') {
      if (window.location.pathname !== '/grading/leaderboard' && window.location.pathname !== '/leaderboard') {
        window.history.replaceState(null, '', '/grading/leaderboard');
      }
    } else if (activeTab === 'grading' || activeTab === 'ujian-grading') {
      if (!window.location.pathname.startsWith('/grading') && !window.location.pathname.startsWith('/ujian-grading')) {
        window.history.replaceState(null, '', '/grading');
      }
    } else if (activeTab === 'pengurusan-silibus' || activeTab === 'syllabus-manage' || activeTab === 'kurikulum') {
      if (!window.location.pathname.startsWith('/pengurusan-silibus') && !window.location.pathname.startsWith('/syllabus/manage') && !window.location.pathname.startsWith('/kurikulum')) {
        window.history.replaceState(null, '', '/pengurusan-silibus');
      }
    } else {
      const targetPath = `/${activeTab}`;
      if (window.location.pathname !== targetPath) {
        window.history.replaceState(null, '', targetPath);
      }
    }
  }, [activeTab]);

  // Redirect logged-in user away from login or register page directly to intended destination or dashboard
  useEffect(() => {
    if (currentUser && (activeTab === 'login' || activeTab === 'register')) {
      navigateAfterLogin();
    }
  }, [currentUser, activeTab, navigateAfterLogin]);

  // Redirect unauthenticated user away from protected tabs to login, preserving intended URL
  useEffect(() => {
    if (!currentUser && !PUBLIC_TABS.includes(activeTab)) {
      // Check session vault to ensure user isn't in mid-recovery from persistent cookie or storage
      const vaultUser = getStoredUserSession();
      if (vaultUser) {
        return;
      }
      const fullIntended = window.location.pathname + window.location.search;
      if (fullIntended && fullIntended !== '/' && !fullIntended.startsWith('/login') && !fullIntended.startsWith('/register')) {
        sessionStorage.setItem('asmm_intended_url', fullIntended);
      }
      handleTabChange('login');
    }
  }, [currentUser, activeTab, handleTabChange]);

  // Access Guard: Restrict /audit to Executive Committee only (Ahli biasa cannot access, Superadmin has bypass)
  useEffect(() => {
    if (activeTab === 'audit' && currentUser) {
      const allowedAuditRoles = ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'Pemeriksa Kira-Kira'];
      if (!hasRolePermission(currentUser, allowedAuditRoles)) {
        handleTabChange('dashboard');
      }
    }
  }, [currentUser, activeTab, handleTabChange]);

  // Access Guard: Restrict /roles to Management Level only (Jurulatih, Ketua Gelanggang, AJK & Superadmin)
  useEffect(() => {
    if (activeTab === 'roles' && currentUser) {
      if (!isManagementOrTrainer(currentUser)) {
        handleTabChange('members');
      }
    }
  }, [currentUser, activeTab, handleTabChange]);

  // Access Guard: Restrict /grading/settings to Management Level only (Superadmin bypasses)
  useEffect(() => {
    if (activeTab === 'grading-settings' && currentUser) {
      if (!isManagementOrTrainer(currentUser)) {
        showToast(
          'error',
          'Akses Ditolak',
          'Halaman Tetapan Ujian Bengkung hanya boleh diakses oleh pihak pengurusan persatuan.'
        );
        handleTabChange('grading');
      }
    }
  }, [currentUser, activeTab, handleTabChange, showToast]);

  // Access Guard: Restrict /pengurusan-silibus to Management Level & Trainers only (Superadmin bypasses)
  useEffect(() => {
    if ((activeTab === 'pengurusan-silibus' || activeTab === 'syllabus-manage' || activeTab === 'kurikulum') && currentUser) {
      if (!isManagementOrTrainer(currentUser)) {
        showToast(
          'error',
          'Akses Ditolak',
          'Halaman Pengurusan Kurikulum & Silibus hanya boleh diakses oleh pihak pengurusan persatuan & jurulatih.'
        );
        handleTabChange('syllabus');
      }
    }
  }, [currentUser, activeTab, handleTabChange, showToast]);

  // Access Guard: Restrict /tabung to Management Level only (Superadmin bypasses)
  useEffect(() => {
    if (activeTab === 'tabung' && currentUser) {
      const allowedTabungRoles = ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'AJK'];
      if (!hasRolePermission(currentUser, allowedTabungRoles)) {
        handleTabChange('dashboard');
      }
    }
  }, [currentUser, activeTab, handleTabChange]);

  // Access Guard: When onboarding payment is pending admin confirmation, only allow dashboard, payments, and account
  useEffect(() => {
    if (currentUser && isPaymentPendingAdminConfirmation(currentUser, invoices, members)) {
      const ALLOWED_WHEN_PENDING = ['dashboard', 'payments', 'account', ...PUBLIC_TABS];
      if (!ALLOWED_WHEN_PENDING.includes(activeTab)) {
        handleTabChange('payments');
      }
    }
  }, [currentUser, invoices, members, activeTab, handleTabChange]);

  const isManagement = currentUser && isManagementOrTrainer(currentUser);
  const [isMembersSubmenuOpen, setIsMembersSubmenuOpen] = useState<boolean>(() => {
    return ['members', 'roles'].includes(getInitialTab());
  });
  const [isGradingSubmenuOpen, setIsGradingSubmenuOpen] = useState<boolean>(() => {
    return ['grading', 'ujian-grading', 'grading-settings', 'grading-leaderboard', 'leaderboard', 'ujian-bertulis'].includes(getInitialTab());
  });
  const [isSyllabusSubmenuOpen, setIsSyllabusSubmenuOpen] = useState<boolean>(() => {
    return ['syllabus', 'pengurusan-silibus', 'syllabus-manage', 'kurikulum'].includes(getInitialTab());
  });

  useEffect(() => {
    if (['members', 'roles'].includes(activeTab)) {
      setIsMembersSubmenuOpen(true);
    }
    if (['grading', 'ujian-grading', 'grading-settings', 'grading-leaderboard', 'leaderboard', 'ujian-bertulis'].includes(activeTab)) {
      setIsGradingSubmenuOpen(true);
    }
    if (['syllabus', 'pengurusan-silibus', 'syllabus-manage', 'kurikulum'].includes(activeTab)) {
      setIsSyllabusSubmenuOpen(true);
    }
  }, [activeTab]);

  interface SidebarSubItem {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    managementOnly?: boolean;
    badge?: string;
  }

  interface SidebarItem {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    familyOnly?: boolean;
    roles?: string[];
    subItems?: SidebarSubItem[];
  }

  const sidebarItems: SidebarItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'keluarga', label: 'Keluarga', icon: Users, familyOnly: true },
    { id: 'gelanggang', label: 'Gelanggang', icon: Building },
    {
      id: 'syllabus',
      label: 'Silibus',
      icon: BookOpen,
      subItems: isManagement
        ? [
          { id: 'syllabus', label: 'Pengajian Silibus', icon: BookOpen },
          { id: 'pengurusan-silibus', label: 'Pengurusan Silibus', icon: Settings, managementOnly: true },
        ]
        : undefined
    },
    {
      id: 'grading',
      label: 'Penilaian Ujian',
      icon: Award,
      subItems: [
        { id: 'grading', label: 'Pentaksiran Calon', icon: Award },
        { id: 'ujian-bertulis', label: 'Ujian Bertulis', icon: FileText },
        { id: 'grading-leaderboard', label: 'Leaderboard', icon: Trophy },
        { id: 'grading-settings', label: 'Tetapan', icon: Settings, managementOnly: true },
      ]
    },
    { id: 'payments', label: 'Bayaran', icon: CreditCard },
    { id: 'notices', label: 'Notis', icon: Bell },
    { id: 'audit', label: 'Audit', icon: FileSpreadsheet, roles: ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'Pemeriksa Kira-Kira'] },
    ...(isSuperAdmin(currentUser) ? [
      { id: 'grants', label: 'Geran', icon: ShieldCheck },
      { id: 'assets', label: 'Aset', icon: FolderArchive },
    ] : []),
    {
      id: 'members',
      label: 'Ahli',
      icon: Users,
      subItems: isSuperAdmin(currentUser)
        ? [
          { id: 'members', label: 'Direktori Ahli', icon: Users },
          { id: 'roles', label: 'Roles Management', icon: ShieldCheck, managementOnly: true },
        ]
        : undefined
    },

    { id: 'pembelian', label: 'Pembelian Saya', icon: ShoppingBag },
    { id: 'inventory', label: 'Inventori', icon: Package, roles: ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'Ketua Gelanggang', 'AJK'] },
    { id: 'tabung', label: 'Tabung', icon: Heart, roles: ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Bendahari', 'AJK'] },
    ...(isSuperAdmin(currentUser)
      ? [{ id: 'settings', label: 'Tetapan', icon: Settings }]
      : []),
    { id: 'account', label: 'Akaun Saya', icon: User },
  ];

  const { logout } = useAuth();
  const showSidebar = currentUser && ['dashboard', 'keluarga', 'notices', 'payments', 'audit', 'grants', 'assets', 'members', 'roles', 'syllabus', 'pengurusan-silibus', 'syllabus-manage', 'kurikulum', 'grading', 'ujian-grading', 'grading-settings', 'grading-leaderboard', 'leaderboard', 'ujian-bertulis', 'account', 'pembelian', 'inventory', 'gelanggang', 'tabung', 'settings'].includes(activeTab);

  // Security Guard 1: Force full-pageview profile completion for incomplete profiles
  // (Portal DOM and sensitive data will NOT mount until personal details are completed)
  if (currentUser && !isProfileComplete(currentUser)) {
    return <CompleteProfilePage />;
  }

  // Security Guard: Force Waris to have at least 1 registered/linked child before accessing dashboard
  if (currentUser && isMembersLoaded && isWarisMissingChild(currentUser, members)) {
    if (!['landing', 'tentang', 'organisasi', 'sejarah', 'silibus-awam', 'hubungi'].includes(activeTab)) {
      return <WarisChildRequiredPage onChildAdded={() => handleTabChange('payments')} />;
    }
  }

  return (
    <div className={`min-h-screen flex flex-col ${showSidebar ? 'bg-slate-50' : 'bg-white'} text-slate-900 selection:bg-asmm-red selection:text-white`}>
      {/* Top Header Navigation */}
      <Navbar activeTab={activeTab} setActiveTab={handleTabChange} isPaymentPending={isPaymentPending} />

      {/* Main Layout Area */}
      {showSidebar ? (
        <div className="flex-1 max-w-[1340px] w-full mx-auto px-3.5 sm:px-6 pt-3.5 sm:pt-6 pb-20 sm:pb-6 flex flex-col lg:flex-row gap-6">
          <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-white border border-slate-200 rounded-3xl p-5 shadow-sm space-y-6 h-fit sticky top-24">
            <div className="space-y-1.5">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 px-3">
                Menu Utama
              </span>
              <nav className="space-y-1">
                {sidebarItems.map((item) => {
                  let isAllowed = hasRolePermission(currentUser, item.roles);
                  if (item.familyOnly) {
                    isAllowed = !!(currentUser && canUserAddChild(currentUser));
                  }
                  if (!isAllowed) return null;
                  const Icon = item.icon;
                  const isParentActive = activeTab === item.id || (item.subItems && item.subItems.some(sub => activeTab === sub.id));
                  const hasSubItems = Boolean(item.subItems && item.subItems.length > 0);
                  const isLockedByPayment = isPaymentPending && !['dashboard', 'payments', 'account'].includes(item.id);
                  const isPaymentTab = item.id === 'payments';

                  if (hasSubItems) {
                    const isSubOpen = item.id === 'members' ? isMembersSubmenuOpen : item.id === 'grading' ? isGradingSubmenuOpen : item.id === 'syllabus' ? isSyllabusSubmenuOpen : false;

                    return (
                      <div key={item.id} className="space-y-1">
                        <button
                          type="button"
                          onClick={() => {
                            if (isLockedByPayment) {
                              handleTabChange(item.id);
                              return;
                            }
                            if (item.id === 'members') {
                              if (!isMembersSubmenuOpen) {
                                setIsMembersSubmenuOpen(true);
                                if (activeTab !== 'members' && activeTab !== 'roles') {
                                  handleTabChange('members');
                                }
                              } else {
                                setIsMembersSubmenuOpen(!isMembersSubmenuOpen);
                              }
                            } else if (item.id === 'grading') {
                              if (!isGradingSubmenuOpen) {
                                setIsGradingSubmenuOpen(true);
                                if (activeTab !== 'grading' && activeTab !== 'grading-settings' && activeTab !== 'grading-leaderboard' && activeTab !== 'ujian-bertulis') {
                                  handleTabChange('grading');
                                }
                              } else {
                                setIsGradingSubmenuOpen(!isGradingSubmenuOpen);
                              }
                            } else if (item.id === 'syllabus') {
                              if (!isSyllabusSubmenuOpen) {
                                setIsSyllabusSubmenuOpen(true);
                                if (activeTab !== 'syllabus' && activeTab !== 'pengurusan-silibus') {
                                  handleTabChange('syllabus');
                                }
                              } else {
                                setIsSyllabusSubmenuOpen(!isSyllabusSubmenuOpen);
                              }
                            }
                          }}
                          className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${isParentActive
                            ? 'text-asmm-red bg-red-50/60 font-extrabold'
                            : isLockedByPayment
                              ? 'text-slate-400 hover:bg-slate-100 opacity-60'
                              : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                            }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-4 h-4 shrink-0 ${isParentActive ? 'text-asmm-red' : isLockedByPayment ? 'text-slate-400' : 'text-slate-500'}`} />
                            <span>{item.label}</span>
                          </div>
                          {isLockedByPayment ? (
                            <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                          ) : isSubOpen ? (
                            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                          )}
                        </button>

                        {isSubOpen && !isLockedByPayment && (
                          <div className="pl-3.5 ml-4 border-l-2 border-slate-200/80 space-y-1 py-0.5 animate-fade-in">
                            {item.subItems!
                              .filter((sub) => !sub.managementOnly || isManagement)
                              .map((sub) => {
                                const SubIcon = sub.icon;
                                const isSubActive = activeTab === sub.id;

                                return (
                                  <button
                                    key={sub.id}
                                    type="button"
                                    onClick={() => handleTabChange(sub.id)}
                                    className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${isSubActive
                                      ? 'text-asmm-red bg-red-100/70 font-black shadow-2xs'
                                      : 'text-slate-600 hover:bg-slate-100/80 hover:text-slate-900'
                                      }`}
                                  >
                                    <div className="flex items-center gap-2.5 truncate">
                                      <SubIcon className={`w-3.5 h-3.5 shrink-0 ${isSubActive ? 'text-asmm-red' : 'text-slate-400'}`} />
                                      <span className={isSubActive ? 'underline underline-offset-4 decoration-asmm-red decoration-2' : ''}>
                                        {sub.label}
                                      </span>
                                    </div>
                                  </button>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleTabChange(item.id)}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer border-none ${isActive
                        ? 'text-asmm-red bg-red-50/60 font-extrabold'
                        : isLockedByPayment
                          ? 'text-slate-400 hover:bg-slate-100 opacity-60'
                          : 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
                        }`}
                      title={isLockedByPayment ? 'Dikunci: Menunggu Pengesahan Bayaran Pentadbir' : undefined}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-asmm-red' : isLockedByPayment ? 'text-slate-400' : 'text-slate-500'}`} />
                        <span className={isActive ? 'underline underline-offset-4 decoration-asmm-red decoration-2' : ''}>
                          {item.label}
                        </span>
                      </div>
                      {isLockedByPayment && (
                        <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      )}
                      {isPaymentTab && isPaymentPending && (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider shrink-0 ${paymentStatus.hasUploadedReceipt
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-red-600 text-white animate-pulse'
                          }`}>
                          {paymentStatus.hasUploadedReceipt ? 'Pengesahan' : 'Bayar'}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Main Workspace content */}
          <main className="flex-1 min-w-0">
            {activeTab === 'dashboard' && (
              <>
                <SEO title="Dashboard Ahli" description="Panel kawalan ahli dan pengurusan Persatuan Seni Silat Awang Sulung Merah Muda Kedah." />
                <DashboardPage setActiveTab={handleTabChange} />
              </>
            )}
            {activeTab === 'keluarga' && (
              <>
                <SEO title="Keluarga Saya" description="Uruskan profil ahli keluarga dan pembayaran yuran keluarga secara berpusat." />
                <KeluargaPage />
              </>
            )}
            {activeTab === 'notices' && (
              <>
                <SEO title="Pengumuman & Notis Acara" description="Papan pengumuman rasmi pertubuhan, notis latihan gelanggang, dan RSVP aktiviti ASMM." />
                <NoticeManagementPage />
              </>
            )}
            {activeTab === 'payments' && (
              <>
                <SEO title="Portal Yuran & Sumbangan FPX" description="Sistem pembayaran yuran bulanan gelanggang, pendaftaran ujian bengkung, dan sumbangan tabung ASMM." />
                <PaymentPage />
              </>
            )}
            {activeTab === 'audit' && (
              <>
                <SEO title="Laporan Kewangan Audit" description="Penyata audit kewangan rasmi pertubuhan ROS Persatuan Seni Silat Awang Sulung Merah Muda." />
                <AuditReportPage />
              </>
            )}
            {activeTab === 'grants' && (
              isSuperAdmin(currentUser) ? (
                <>
                  <SEO title="Pengurusan Geran & Elaun" description="Permohonan geran kementerian, pengagihan belanjawan sah, dan elaun kejurulatihan." />
                  <GrantManagementPage />
                </>
              ) : (
                <DashboardPage setActiveTab={handleTabChange} />
              )
            )}
            {activeTab === 'assets' && (
              isSuperAdmin(currentUser) ? (
                <>
                  <SEO title="Inventori Aset & Sewaan" description="Perekodan aset kelengkapan silat, mat gelanggang, dan sewaan penjanaan pendapatan NGO." />
                  <AssetRentalPage />
                </>
              ) : (
                <DashboardPage setActiveTab={handleTabChange} />
              )
            )}
            {activeTab === 'members' && (
              <>
                <SEO title="Direktori Ahli Pesilat" description="Pangkalan data Direktori Ahli dan Barisan Jawatankuasa Pengurusan ASMM Kedah." />
                <MemberDirectoryPage />
              </>
            )}
            {activeTab === 'roles' && (
              isSuperAdmin(currentUser) ? (
                <>
                  <SEO title="Roles Management" description="Roles Management dan penetapan peranan jawatankuasa tadbir urus ASMM." />
                  <RolesManagementPage />
                </>
              ) : (
                <>
                  <SEO title="Direktori Ahli Pesilat" description="Pangkalan data Direktori Ahli dan Barisan Jawatankuasa Pengurusan ASMM Kedah." />
                  <MemberDirectoryPage />
                </>
              )
            )}
            {activeTab === 'gelanggang' && (
              <>
                <SEO title="Info & Tetapan Gelanggang" description="Sesi kelas latihan, aktiviti ditawarkan, yuran, dan pengurusan pentadbiran gelanggang ASMM." />
                <GelanggangPage />
              </>
            )}
            {activeTab === 'syllabus' && (
              <>
                <SEO title="Pengajian Silibus Ahli" description="Modul pembelajaran kurikulum silat, panduan fizikal bengkung, dan perlembagaan perguruan ASMM." />
                <SyllabusPage />
              </>
            )}
            {activeTab === 'pengurusan-silibus' && (
              <>
                <SEO title="Pengurusan Kurikulum & Silibus" description="Pengurusan modul sukatan pelajaran silat, video kurikulum dan syarat kelayakan bengkung ASMM." />
                <PengurusanSilibusPage />
              </>
            )}
            {(activeTab === 'grading' || activeTab === 'ujian-grading') && (
              <>
                <SEO title="Penilaian Ujian Bengkung" description="Pengurusan dan pentaksiran rubrik ujian kenaikan bengkung calon berbayar ASMM." />
                <UjianGradingPage />
              </>
            )}
            {activeTab === 'grading-settings' && (
              <>
                <SEO title="Tetapan Ujian Bengkung" description="Konfigurasi silibus, markah lulus minima dan dewan penguji ASMM." />
                <UjianGradingSettingsPage />
              </>
            )}
            {activeTab === 'ujian-bertulis' && (
              <>
                <SEO title="Ujian Bertulis PSSASMM" description="Ujian bertulis objektif 50 soalan berkaitan sahsiah beladiri, silat, dan persatuan ASMM." />
                <UjianBertulisPage />
              </>
            )}
            {(activeTab === 'grading-leaderboard' || activeTab === 'leaderboard') && (
              <>
                <SEO title="Papan Skor & Kedudukan (Leaderboard) Ujian Bengkung" description="Papan kedudukan rasmi markah pentaksiran ujian kenaikan bengkung calon ASMM." />
                <UjianGradingLeaderboardPage />
              </>
            )}
            {activeTab === 'account' && (
              <>
                <SEO title="Profil Akaun Saya" description="Maklumat profil pesilat, gelanggang latihan, dan status bengkung ASMM." />
                <AccountPage />
              </>
            )}
            {activeTab === 'merchandise' && (
              <>
                <SEO title="Kedai Merchandise" description="Tempahan pakaian silat rasmi, keychain, tshirt, bengkung dan tanjak persatuan." />
                <MerchandisePage onNavigateTab={handleTabChange} />
              </>
            )}
            {activeTab === 'pembelian' && (
              <>
                <SEO title="Pembelian Saya" description="Senarai semua tempahan anda." />
                <PembelianPage />
              </>
            )}
            {activeTab === 'inventory' && (
              <>
                <SEO title="Inventori Kedai" description="Pengurusan barangan jualan anda." />
                <InventoriPage />
              </>
            )}
            {activeTab === 'landing' && (
              <>
                <SEO title="Laman Utama" description="Portal Rasmi Persatuan Seni Silat Awang Sulung Merah Muda Negeri Kedah (ROS: PPM-007-02-28082022). Pusat latihan seni silat tradisi Melayu dan pengurusan pertubuhan." />
                <LandingPage onGoToLogin={() => handleTabChange('dashboard')} />
              </>
            )}
            {activeTab === 'dokumen' && (
              <>
                <SEO title="Arkib Dokumen" description="Arkib fail perlembagaan pertubuhan, minit mesyuarat agung, dan dokumen rasmi ROS Kedah." />
                <DocumentArchivePage />
              </>
            )}
            {activeTab === 'tentang' && (
              <>
                <SEO title="Tentang Persatuan" description="Pengenalan, maksud lambang, visi, misi dan carta organisasi rasmi Persatuan Seni Silat Awang Sulung Merah Muda." />
                <AboutPage />
              </>
            )}
            {activeTab === 'organisasi' && (
              <>
                <SEO title="Carta Organisasi" description="Carta organisasi AJK Rasmi JPPM Persatuan Seni Silat Awang Sulung Merah Muda Kedah." />
                <OrgChartPage />
              </>
            )}
            {activeTab === 'sejarah' && (
              <>
                <SEO title="Sejarah & Asal Usul Perguruan" description="Sejarah asal usul Persatuan Seni Silat Awang Sulung Merah Muda, salasilah guru-guru dan perkembangan perguruan di Kedah." />
                <HistoryPage />
              </>
            )}
            {activeTab === 'silibus-awam' && (
              <>
                <SEO title="Silibus & Kurikulum Bengkung" description="Silibus kurikulum bengkung ASMM meliputi Bengkung Putih, Hitam, Merah, Biru, dan Kuning." />
                <PublicSyllabusPage />
              </>
            )}
            {activeTab === 'hubungi' && (
              <>
                <SEO title="Hubungi & Pendaftaran Gelanggang" description="Pendaftaran pesilat baru dan lokasi gelanggang latihan ASMM (Pusaka Warisan, Wadi AnNour, Paya Besar)." />
                <ContactPage />
              </>
            )}
            {activeTab === 'tabung' && (
              <>
                <SEO title="Pengurusan Tabung Infaq" description="Panel pengurusan kempen tabung, rekod infaq dan penjanaan resit sumbangan rasmi ASMM." />
                <DonationsManagementPage onNavigateToPublic={() => handleTabChange('donations')} />
              </>
            )}
            {activeTab === 'settings' && (
              isSuperAdmin(currentUser) ? (
                <>
                  <SEO title="Tetapan Sistem & Yuran" description="Konfigurasi tetapan sistem dan yuran ujian bengkung ASMM oleh Superadmin." />
                  <SettingsPage />
                </>
              ) : (
                <DashboardPage setActiveTab={handleTabChange} />
              )
            )}
            {(activeTab === 'ujian-bengkung' || activeTab === 'ujian') && (
              <>
                <SEO title="Pendaftaran Ujian Bengkung & Tempahan Keluarga" description="Pendaftaran Ujian Kenaikan Bengkung ASMM dan tempahan kemudahan keluarga sekeluarga." />
                <UjianBengkungPage onNavigateToTab={handleTabChange} />
              </>
            )}
          </main>
        </div>
      ) : (
        <main className="main-content">
          {(activeTab === 'login' || activeTab === 'register') && (
            <>
              <SEO
                title={activeTab === 'register' ? "Pendaftaran Portal Ahli" : "Log Masuk Portal Ahli"}
                description={activeTab === 'register' ? "Pendaftaran portal rasmi pesilat dan pengurusan Persatuan Seni Silat Awang Sulung Merah Muda Kedah." : "Log masuk portal rasmi pesilat dan pengurusan Persatuan Seni Silat Awang Sulung Merah Muda Kedah."}
              />
              <LoginPage
                key={activeTab}
                initialMode={activeTab as 'login' | 'register'}
                onLoginSuccess={navigateAfterLogin}
                onModeChange={(mode) => handleTabChange(mode)}
              />
            </>
          )}
          {activeTab === 'account' && (
            <>
              <SEO title="Profil Akaun Saya" description="Maklumat profil pesilat, gelanggang latihan, dan status bengkung ASMM." />
              <AccountPage />
            </>
          )}
          {activeTab === 'landing' && (
            <>
              <SEO title="Laman Utama" description="Portal Rasmi Persatuan Seni Silat Awang Sulung Merah Muda Negeri Kedah (ROS: PPM-007-02-28082022). Pusat latihan seni silat tradisi Melayu dan pengurusan pertubuhan." />
              <LandingPage onGoToLogin={() => handleTabChange('login')} />
            </>
          )}
          {activeTab === 'dokumen' && (
            <>
              <SEO title="Arkib Dokumen" description="Arkib fail perlembagaan pertubuhan, minit mesyuarat agung, dan dokumen rasmi ROS Kedah." />
              <DocumentArchivePage />
            </>
          )}
          {activeTab === 'tentang' && (
            <>
              <SEO title="Tentang Persatuan" description="Pengenalan, maksud lambang, visi, misi dan carta organisasi rasmi Persatuan Seni Silat Awang Sulung Merah Muda." />
              <AboutPage />
            </>
          )}
          {activeTab === 'organisasi' && (
            <>
              <SEO title="Carta Organisasi" description="Carta organisasi AJK Rasmi JPPM Persatuan Seni Silat Awang Sulung Merah Muda Kedah." />
              <OrgChartPage />
            </>
          )}
          {activeTab === 'sejarah' && (
            <>
              <SEO title="Sejarah & Asal Usul Perguruan" description="Sejarah asal usul Persatuan Seni Silat Awang Sulung Merah Muda, salasilah guru-guru dan perkembangan perguruan di Kedah." />
              <HistoryPage />
            </>
          )}
          {activeTab === 'silibus-awam' && (
            <>
              <SEO title="Silibus & Kurikulum Bengkung" description="Silibus kurikulum bengkung ASMM meliputi Bengkung Putih, Hitam, Merah, Biru, dan Kuning." />
              <PublicSyllabusPage />
            </>
          )}
          {activeTab === 'hubungi' && (
            <>
              <SEO title="Hubungi & Pendaftaran Gelanggang" description="Pendaftaran pesilat baru dan lokasi gelanggang latihan ASMM (Pusaka Warisan, Wadi AnNour, Paya Besar)." />
              <ContactPage />
            </>
          )}
          {activeTab === 'payments' && (
            <>
              <SEO title="Semakan & Pembayaran Invois FPX" description="Semak dan bayar invois rasmi PSSASMM tanpa perlu log masuk. Bayaran selamat melalui ToyyibPay FPX." />
              <PaymentPage />
            </>
          )}
          {activeTab === 'merchandise' && (
            <>
              <SEO title="Kedai" description="Katalog barangan rasmi persatuan silat ASMM." />
              <MerchandisePage />
            </>
          )}
          {(activeTab === 'donations' || activeTab === 'donasi') && (
            <>
              <SEO title="Tabung Infaq & Pembangunan" description="Sumbangan infaq, wakaf dan dana pembangunan Persatuan Seni Silat Awang Sulung Merah Muda Kedah (ROS: PPM-007-02-28082022)." />
              <DonationPublicPage onNavigateToDashboard={() => handleTabChange('tabung')} />
            </>
          )}
          {(activeTab === 'ujian-bengkung' || activeTab === 'ujian') && (
            <>
              <SEO title="Pendaftaran Ujian Bengkung & Tempahan Keluarga" description="Pendaftaran Ujian Kenaikan Bengkung ASMM dan tempahan kemudahan keluarga sekeluarga." />
              <UjianBengkungPage onNavigateToTab={handleTabChange} />
            </>
          )}
          {activeTab === 'ujian-bertulis' && (
            <>
              <SEO title="Ujian Bertulis PSSASMM" description="Ujian bertulis teori 50 soalan objektif berkaitan sahsiah beladiri, silat, dan persatuan ASMM." />
              <UjianBertulisPage />
            </>
          )}
          {(activeTab === 'onboarding-payment' || activeTab === 'onboarding') && (
            <OnboardingPaymentPage onPaymentCompleted={() => handleTabChange('dashboard')} />
          )}
        </main>
      )}

      {/* Floating Gear Role Switcher (Right side widget) */}
      <RoleSwitcherFloating />

      {/* Floating Live Donation Popup Notification Toast - Only on Tabung Infaq /donations and its sub pages */}
      {(activeTab === 'donations' || activeTab === 'donasi') && (
        <DonationLiveToastPopup hasBottomNav={Boolean(showSidebar)} />
      )}

      {/* Push Notification Subscription Prompt (re-asks every 12h if dismissed) */}
      <PushSubscribePrompt />

      {/* Clean Footer */}
      <footer className={`w-full border-t border-slate-200 py-6 bg-white no-print ${showSidebar ? 'hidden sm:block' : ''}`}>
        <div className="max-w-[1340px] mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-xs text-slate-500">
          <div className="flex items-center gap-2 text-center sm:text-left">
            <img src="/logo-pssasmm.png" alt="ASMM" className="h-6 w-auto object-contain shrink-0" />
            <span>© 2026 PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA (PPM-007-02-28082022)</span>
          </div>
          <div className="flex items-center gap-2">
            {SOCIAL_LINKS.map((s) => (
              <a
                key={s.id}
                id={`footer-social-${s.id}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                title={s.label}
                className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:text-white hover:bg-slate-900 hover:border-slate-900 hover:-translate-y-0.5 transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                  <path d={s.path} />
                </svg>
              </a>
            ))}
          </div>
        </div>
      </footer>

      {/* Global Tagline Philosophy Modal (Triggered via Footer / Public links) */}
      <TaglinePhilosophyModal
        isOpen={isTaglineModalOpen}
        onClose={() => setIsTaglineModalOpen(false)}
      />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ToastProvider>
  );
};

export default App;
